<!--
  One task's queue: what is left, in order, and nothing else.

  There are no filter controls here, and their absence is the design. A work queue answers
  "what next", which is a question about ordering; "show me the records like this" is a
  different question and the corpus browser answers it. The seven controls that used to sit
  here are there now.

  What a row is: a record with work left for *this* task, carrying the image the task would
  open. A finished record leaves the list rather than sitting in it with a tick — going back to
  a finished stone to fix a box is the browser's job, and the pager's.

  A row may also carry `reasons`: the server's own sentences for why the record is here (the
  second pass names the unsettled chips and the disagreeing copies). They are printed as given,
  in words, under the row — never summarised into a colour or an icon, and never derived here.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import { href, router } from '../lib/router.svelte';
  import { displaySiglum } from '../lib/nav';
  import { app } from '../lib/stores/app.svelte';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { locks } from '../lib/stores/locks.svelte';
  import { queue } from '../lib/stores/queue.svelte';
  import { itemReasons, taskItemRoute, taskView } from '../lib/taskViews';
  import type { TaskQueueItem } from '../lib/api/types';
  import LockPanel from './LockPanel.svelte';
  import ProgressMeter from './ProgressMeter.svelte';

  interface Props {
    /** The task id from the URL. Resolved against the server's registry, never enumerated. */
    taskId: string;
  }

  const { taskId }: Props = $props();

  const lockedByInscription = $derived(locks.byInscription);
  const view = $derived(taskView(taskId));
  const card = $derived(queue.task);

  $effect(() => {
    const id = taskId;
    untrack(() => {
      void queue.ensureCards();
      void queue.select(id);
    });
  });

  /**
   * Whose rows can be started.
   *
   * A live lock held by somebody else is shown rather than hidden: they are mid-inscription,
   * and finding that out by clicking Annotate and reading a 409 is the friction this badge
   * exists to remove.
   */
  function liveLockHeldByOther(item: TaskQueueItem): boolean {
    return (lockedByInscription.get(item.id) ?? []).some(
      (entry) => entry.lock.held && !entry.held_by_caller,
    );
  }

  function open(item: TaskQueueItem): void {
    const route = taskItemRoute(taskId, item.next_image_id);
    if (route) router.go(route);
    else router.go({ name: 'inscription', inscriptionId: item.id });
  }

  /** The next row nobody else is holding. What "Start next" means. */
  const nextStartable = $derived(queue.items.find((item) => !liveLockHeldByOther(item)) ?? null);
  const nextReasons = $derived(itemReasons(nextStartable));

  /**
   * An inscription-unit task counts records: one look (or one fix) answers it for every
   * photograph of the stone, so "1 of 3 image(s) left" would name the wrong unit. Same rule as
   * the chooser's cards.
   */
  const perRecord = $derived(card?.progress.unit === 'inscription');
</script>

<div class="queue col">
  <div class="row wrap head">
    <div class="col" style="gap:2px">
      <h2>{card?.label ?? 'Work queue'}</h2>
      <p class="small muted">
        {#if card}
          {card.description}
        {/if}
      </p>
    </div>
    <span class="spacer"></span>
    <a class="small" href={href({ name: 'queue', task: null })}>← All tasks</a>
  </div>

  {#if app.blockers.length}
    <div class="banner error col">
      <strong>The tool is not ready to annotate</strong>
      <ul>
        {#each app.blockers as blocker, index (index)}
          <li class="small">{blocker}</li>
        {/each}
      </ul>
      <p class="small muted">
        These come straight from <span class="mono">GET /api/settings/status</span>. Run the
        commands above from <span class="mono">tools/hismaic_annotator/</span>, or set a bucket
        in <a href={href({ name: 'settings' })}>Settings</a>.
      </p>
      <div class="row">
        <button onclick={() => void app.refreshStatus()}>Re-check</button>
      </div>
    </div>
  {/if}

  {#if card?.blocked_reason}
    <div class="banner warn col">
      <strong>Nothing is scoped to this task yet</strong>
      <p class="small">{card.blocked_reason}</p>
    </div>
  {/if}

  {#if !view}
    <div class="banner warn small">
      This build has no screen for <span class="mono">{taskId}</span>, so its rows cannot be
      opened here. The service offers the task; the frontend is older than the API.
    </div>
  {/if}

  <!--
    One button, naming the stone it opens — and first on the page.

    This used to be a `Start next` styled exactly like `Refresh` beside it, above a table of
    sixty equally-weighted `Box` buttons — so "what do I do next?" was answered with sixty
    options and no ranking. The queue already computes the answer (`nextStartable`); it just
    was not saying it out loud. The table is still here, in order and entire, one disclosure
    down: picking a particular stone is a real thing to want, just not the common thing.

    Order matters as much as prominence. The meters and the lock table used to sit above this,
    and with a lock or two showing they pushed the only button anybody came here to press off
    the bottom of the screen. Progress is worth reading; it is not worth reading *first*.
  -->
  {#if !card?.blocked_reason}
    <div class="panel col next-up">
      {#if nextStartable}
        <span class="small muted">Next up</span>
        <button
          class="primary start"
          onclick={() => open(nextStartable)}
          disabled={!view || !annotator.ready || nextStartable.next_image_id === null}
          aria-describedby={nextReasons.length ? 'next-up-reasons' : undefined}
        >
          {view?.action ?? 'Open'}
          {displaySiglum(nextStartable)}
        </button>
        <p class="small muted">
          {#if perRecord}
            {nextStartable.token_count} expected letter(s) · {nextStartable.image_count} image(s),
            one record{nextStartable.seed_batch ? ' · seed batch' : ''}
          {:else}
            {nextStartable.token_count} expected letter(s) · {nextStartable.pending_images} of {nextStartable.image_count}
            image(s) left{nextStartable.seed_batch ? ' · seed batch' : ''}
          {/if}
        </p>
        {#if nextReasons.length}
          <div class="col next-reasons">
            <span class="small">Why it is here</span>
            <ul
              class="reasons small"
              id="next-up-reasons"
              aria-label="Why {displaySiglum(nextStartable)} is in this queue"
            >
              {#each nextReasons as reason, index (index)}
                <li>{reason}</li>
              {/each}
            </ul>
          </div>
        {/if}
        {#if !annotator.ready}
          <p class="small warn-text">
            {#if annotator.readOnlyAccount}
              This account is read-only, so nothing here can be saved. An administrator can
              change the role.
            {:else}
              Nothing can be saved until this session has an annotator id.
            {/if}
          </p>
        {/if}
      {:else if queue.loading}
        <span class="small muted">Finding the next record…</span>
      {:else if queue.items.length}
        <strong class="small">Every record on this page is locked by somebody else right now.</strong>
        <p class="small muted">
          A lock expires on its own; the table below says who holds each one and for how long.
        </p>
      {:else}
        <strong class="small">Nothing left in this task</strong>
        <p class="small muted">
          Nothing in {card?.scope_label ?? 'its scope'} is waiting on it. Pick
          <a href={href({ name: 'queue', task: null })}>another task</a>, or browse the records
          outside the scope in the <a href={href({ name: 'browse' })}>corpus browser</a>.
        </p>
      {/if}
    </div>
  {/if}

  {#if queue.problem}
    <div class="banner error small">
      <strong>Could not load the queue</strong>
      <p>{queue.problem.message}</p>
      <p class="muted mono">code: {queue.problem.code}</p>
    </div>
  {/if}

  {#if card && !card.blocked_reason}
    <div class="panel col">
      <h3>{card.label} · {card.scope_label}</h3>
      <div class="meters">
        {#each card.progress.meters as meter (meter.key)}
          <ProgressMeter
            label={meter.label}
            value={meter.value}
            total={meter.total}
            detail={meter.detail}
            tone={meter.total > 0 && meter.value >= meter.total ? 'ok' : 'accent'}
          />
        {/each}
      </div>
      <p class="small muted">
        These count {card.scope_label} — {card.progress.inscriptions} records and
        {card.progress.images} images — and nothing else. Records outside it are in the
        <a href={href({ name: 'browse' })}>corpus browser</a>.
      </p>
    </div>
  {/if}

  <LockPanel onchanged={() => void queue.load()} />

  <!-- Not `.col`: a flex `<details>` leaks its content past the closed state in some engines. -->
  <details class="panel records">
    <summary>
      Or pick a particular record
      <span class="muted">
        — {queue.total} with work left{queue.total > queue.pageSize
          ? `, ${queue.pageSize} per page`
          : ''}
      </span>
    </summary>

    <div class="row">
      <span class="spacer"></span>
      <button class="subtle" onclick={() => void queue.load()} disabled={queue.loading}>
        Refresh
      </button>
    </div>

    <table>
      <thead>
        <tr>
          <th>Siglum</th>
          <th>Letters</th>
          <!-- Per-record tasks owe one unit per row, so the useful number is the photographs. -->
          <th>{perRecord ? 'Images' : 'Left'}</th>
          <th>Boxes</th>
          <th>Flags</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {#each queue.items as item (item.id)}
          {@const reasons = itemReasons(item)}
          <tr class:has-reasons={reasons.length > 0}>
            <td>
              <div class="col" style="gap:0">
                <span title={item.siglum_raw}>{displaySiglum(item)}</span>
                <span class="small muted mono">{item.ociana_id ?? ''}</span>
              </div>
            </td>
            <td class="mono">{item.token_count}</td>
            <td class="mono">
              {perRecord ? item.image_count : `${item.pending_images}/${item.image_count}`}
            </td>
            <td class="mono">{item.annotation_count}</td>
            <td class="small">
              {#if item.seed_batch}<span class="tag">seed</span>{/if}
              {#if item.split}<span class="tag">{item.split}</span>{/if}
              {#if !item.has_transliteration}<span class="tag warn">no transliteration</span>{/if}
              {#if item.siglum_shared_with.length}
                <span class="tag warn" title="This siglum key names more than one record">
                  shared siglum
                </span>
              {/if}
              <!-- Colour is never the only signal: the word "locked"/"stale lock" carries it. -->
              {#each lockedByInscription.get(item.id) ?? [] as lock (lock.image_id)}
                <span
                  class="tag"
                  class:warn={lock.lock.held}
                  title={`image #${lock.image_id} is locked by ${lock.lock.locked_by_name ?? lock.lock.locked_by}${
                    lock.lock.held ? '' : ' (expired — anyone can reclaim it)'
                  }`}
                >
                  {lock.lock.held ? 'locked' : 'stale lock'}: {lock.lock.locked_by_name ??
                    lock.lock.locked_by}{lock.held_by_caller ? ' (you)' : ''}
                </span>
              {/each}
            </td>
            <td>
              <button
                onclick={() => open(item)}
                disabled={!view || item.next_image_id === null || !annotator.ready}
                aria-describedby={reasons.length ? `reasons-${item.id}` : undefined}
              >
                {view?.action ?? 'Open'}
              </button>
            </td>
          </tr>
          {#if reasons.length}
            <!-- Under its row, full width: a reason is a sentence, and a sentence needs room. -->
            <tr class="reasons-row">
              <td colspan="6">
                <ul
                  class="reasons small"
                  id={`reasons-${item.id}`}
                  aria-label="Why {displaySiglum(item)} is in this queue"
                >
                  {#each reasons as reason, index (index)}
                    <li>{reason}</li>
                  {/each}
                </ul>
              </td>
            </tr>
          {/if}
        {/each}
        {#if !queue.items.length && !queue.loading && !card?.blocked_reason}
          <tr>
            <td colspan="6" class="muted small">
              Nothing left in this task — nothing in {card?.scope_label ?? 'its scope'} is
              waiting on it. Pick <a href={href({ name: 'queue', task: null })}>another task</a>,
              or widen the scope; the records outside it are in the
              <a href={href({ name: 'browse' })}>corpus browser</a>.
            </td>
          </tr>
        {/if}
      </tbody>
    </table>

    <div class="row small">
      <span class="muted">
        {queue.total} record(s) with work left · showing {queue.total === 0
          ? 0
          : queue.offset + 1}–{Math.min(queue.offset + queue.pageSize, queue.total)}
      </span>
      <span class="spacer"></span>
      <button
        onclick={() => void queue.goTo(queue.offset - queue.pageSize)}
        disabled={queue.offset === 0 || queue.loading}
      >
        Previous
      </button>
      <button
        onclick={() => void queue.goTo(queue.offset + queue.pageSize)}
        disabled={queue.offset + queue.pageSize >= queue.total || queue.loading}
      >
        Next
      </button>
    </div>
  </details>
</div>

<style>
  .queue {
    padding: 12px;
    gap: 12px;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
  }

  .next-up {
    gap: 8px;
    align-items: flex-start;
  }

  /* The one button on the screen that should be unmissable. */
  button.start {
    font-size: 1.05rem;
    padding: 10px 18px;
  }

  .warn-text {
    color: var(--warn);
    margin: 0;
  }

  summary {
    cursor: pointer;
    /* The marker is the only affordance saying this opens, so keep it. */
    list-style: revert;
  }

  details[open] summary {
    margin-bottom: 10px;
  }

  .head {
    align-items: flex-start;
  }

  .head p {
    margin: 0;
  }

  .meters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 14px;
  }

  .tag {
    display: inline-block;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    padding: 0 5px;
    margin-right: 4px;
    font-size: var(--text-xs);
  }

  .tag.warn {
    border-color: var(--warn);
    color: var(--warn);
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  /* The reasons row reads as part of the row above it, not as a row of its own. */
  tr.has-reasons td {
    border-bottom: none;
  }

  tr.reasons-row td {
    padding-top: 0;
  }

  tr.has-reasons:hover + tr.reasons-row {
    background: var(--bg-raised);
  }

  ul.reasons {
    overflow-wrap: anywhere;
  }

  ul.reasons li + li {
    margin-top: 2px;
  }

  .next-reasons {
    gap: 2px;
  }
</style>
